import Icon from 'components/atoms/Icon';
import CircularLoader from 'components/molecules/CircularLoader/CircularLoader';
import {
  FieldArray,
  FieldArrayRenderProps,
  useField,
  useFormikContext,
} from 'formik';
import { Orientation, useTraitsQuery } from 'generated/graphql';
import { KeyboardEvent, useMemo } from 'react';
import { TEditSoftSkillFormValues } from 'views/StaticContent/SoftSkills/drawers/SoftSkillDrawer.helpers';
import { TRequiredSelectOption } from 'views/StaticContent/SoftSkills/SoftSkills.helpers';
import {
  Button,
  FormikSelectFormField,
  FormikTextInputField,
  Table,
} from '@spotted-zebra-uk/ui-components';
import styles from './EditSoftSkillTraits.module.scss';
import SoftSkillTabSection from './SoftSkillTabSection';

type TEditSoftSkillTraitsModal = {
  isEditMode: boolean;
};

type TEditSoftSkillTrait = {
  isEditMode: boolean;
  fieldArrayHelpers: FieldArrayRenderProps;
  traitSelectOptions: TRequiredSelectOption<number>[];
  idx: number;
};

const emptyTrait: TEditSoftSkillFormValues['softSkillTraits'] = [
  {
    trait: {
      label: '',
      value: 0,
    },
    orientation: {
      label: 'Standard',
      value: Orientation.Standard,
    },
    weight: 0,
  },
];

const traitTableHeaderData = [
  { key: 'name', header: 'Name' },
  { key: 'weight', header: 'Weight' },
  { key: 'orientation', header: 'Orientation' },
];

const orientationOptions: TRequiredSelectOption<Orientation>[] = [
  {
    label: 'Standard',
    value: Orientation.Standard,
  },
  {
    label: 'Inverted',
    value: Orientation.Inverted,
  },
] as const;

const EditSoftSkillTrait = ({
  isEditMode,
  fieldArrayHelpers,
  traitSelectOptions,
  idx,
}: TEditSoftSkillTrait) => {
  const { values } = useFormikContext<TEditSoftSkillFormValues>();

  const handleCustomWeightsKeyPress = (
    event: KeyboardEvent<HTMLInputElement>
  ) => {
    // need this to prevent keyboard typing
    if (['e', 'E', '+', '-', ','].includes(event.key)) {
      event.preventDefault();
    }
  };
  const isDeleteDisabled =
    values.softSkillTraits.filter(trait => trait?.trait?.label).length === 0;

  return (
    <div key={idx} className={styles.editSoftSkillTraitContainer}>
      <FormikSelectFormField
        className={styles.editSoftSkillTraitSelector}
        label={'Trait'}
        id={`softSkillTraits.${idx}.trait`}
        options={traitSelectOptions}
        isDisabled={!isEditMode}
        useFormikField={useField}
        placeholder="Trait"
      />
      <FormikTextInputField
        className={styles.editSoftSkillTraitWeight}
        label="Weight"
        type="number"
        placeholder="Weight"
        onKeyPress={handleCustomWeightsKeyPress}
        id={`softSkillTraits.${idx}.weight`}
        disabled={!isEditMode}
        useFormikField={useField}
      />
      <FormikSelectFormField
        className={styles.editSoftSkillTraitOrientation}
        label="Orientation"
        placeholder="Orientation"
        id={`softSkillTraits.${idx}.orientation`}
        options={orientationOptions}
        isDisabled={!isEditMode}
        useFormikField={useField}
      />
      {isEditMode && (
        <Button
          onClick={() => fieldArrayHelpers.remove(idx)}
          size="medium"
          variant="negative"
          type="button"
          leftIcon={<Icon icon="delete_outline" className={styles.icon} />}
          disabled={isDeleteDisabled}
        />
      )}
    </div>
  );
};

export const EditSoftSkillTraits = ({
  isEditMode,
}: TEditSoftSkillTraitsModal) => {
  const onAddTrait = (fieldArrayHelpers: FieldArrayRenderProps) => {
    fieldArrayHelpers.push({
      trait: undefined,
      orientation: {
        label: 'Standard',
        value: Orientation.Standard,
      },
      weight: 0,
    });
  };
  const { values } = useFormikContext<TEditSoftSkillFormValues>();

  const displayTraits =
    values.softSkillTraits.length > 0 ? values.softSkillTraits : emptyTrait;

  const { data: traits, loading: traitsLoading } = useTraitsQuery({
    variables: {
      isArchived: false,
    },
    fetchPolicy: 'cache-and-network',
  });

  const traitTableData = useMemo(() => {
    return values.softSkillTraits.map(trait => {
      return {
        id: trait?.trait?.value ?? '',
        name: trait?.trait?.label ?? '',
        weight: trait?.weight ?? 0,
        orientation: trait?.orientation?.label ?? 'Standard',
      };
    });
  }, [values]);

  if (traitsLoading) {
    return <CircularLoader />;
  }

  const traitSelectOptions = (traits?.Traits ?? [])
    .map(
      (trait): TRequiredSelectOption<number> => ({
        value: trait.id,
        label: trait.name,
      })
    )
    .sort((a, b) => (a.label < b.label ? -1 : 1));

  const isAddDisabled =
    values.softSkillTraits.filter(trait => trait?.trait?.label).length === 0;

  return (
    <SoftSkillTabSection
      title="Traits"
      description={
        <span>
          The personal characteristics that combine to form and make up{' '}
          <span className={styles.skillName}>{values.name}</span>.
        </span>
      }
      hasNoItems={values.softSkillTraits.length === 0 && !isEditMode}
      noItemLabel="traits"
    >
      {isEditMode ? (
        <FieldArray
          name={'softSkillTraits'}
          render={fieldArrayHelpers => (
            <>
              <div className={styles.softSkillTraitsContainer}>
                {displayTraits.map((_, idx) => (
                  <EditSoftSkillTrait
                    isEditMode={isEditMode}
                    fieldArrayHelpers={fieldArrayHelpers}
                    idx={idx}
                    traitSelectOptions={traitSelectOptions}
                    key={idx}
                  />
                ))}
              </div>
              <Button
                className={styles.addTraitButton}
                onClick={() => onAddTrait(fieldArrayHelpers)}
                size="medium"
                variant="secondary"
                leftIcon={<Icon icon="add" className={styles.icon} />}
                disabled={isAddDisabled}
              >
                Add
              </Button>
            </>
          )}
        />
      ) : (
        <Table
          headerData={traitTableHeaderData}
          rowData={traitTableData}
          hasPagination={false}
          className={styles.traitTable}
        />
      )}
    </SoftSkillTabSection>
  );
};
